import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { EASE } from "@/lib/motion";

const KEY = "la_preloaded";
const LOGO = "/brand/la-logo-light.webp";

/** Company logo revealed like a drawn plan, shown once per session. */
export const Preloader = () => {
  const reduce = useReducedMotion();
  const [stage, setStage] = useState(() => {
    if (typeof window === "undefined") return "gone";
    return window.sessionStorage.getItem(KEY) ? "gone" : "show";
  });

  useEffect(() => {
    if (stage !== "show") return undefined;
    const t = setTimeout(() => {
      try {
        window.sessionStorage.setItem(KEY, "1");
      } catch (e) {
        /* ignore */
      }
      setStage("leave");
    }, reduce ? 200 : 2200);
    return () => clearTimeout(t);
  }, [stage, reduce]);

  // hard fallback: never block the page longer than 4.5s
  useEffect(() => {
    const t = setTimeout(() => setStage((s) => (s === "gone" ? s : "gone")), 4500);
    return () => clearTimeout(t);
  }, []);

  if (stage === "gone") return null;

  const leaving = stage === "leave";

  return (
    <motion.div
      data-testid="preloader"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-ink text-paper"
      initial={{ y: "0%" }}
      animate={{ y: leaving ? "-100%" : "0%" }}
      transition={{ duration: reduce ? 0.01 : 0.9, ease: EASE }}
      onAnimationComplete={() => {
        if (leaving) setStage("gone");
      }}
    >
      {/* faint construction grid */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 container-x">
        <div className="grid h-full grid-cols-6 lg:grid-cols-12">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className={"h-full border-l border-paper/[0.06] " + (i >= 6 ? "hidden lg:block" : "")} />
          ))}
        </div>
      </div>

      {/* top labels */}
      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="container-x absolute left-0 right-0 top-7 flex justify-between mono-label text-paper/45"
      >
        <span>Generalbauunternehmung</span>
        <span>Neftenbach, Schweiz</span>
      </motion.div>

      {/* logo */}
      <motion.div
        className="relative w-[240px] sm:w-[340px] lg:w-[400px]"
        initial={reduce ? false : { y: 0 }}
        animate={leaving && !reduce ? { y: -30 } : { y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <motion.img
          src={LOGO}
          alt="L&A Projektbau"
          width={1191}
          height={474}
          draggable={false}
          className="block h-auto w-full select-none"
          initial={reduce ? false : { clipPath: "inset(0 100% 0 0)", opacity: 0.001 }}
          animate={{ clipPath: "inset(0 0% 0 0)", opacity: 1 }}
          transition={{ clipPath: { duration: 1.25, ease: EASE, delay: 0.2 }, opacity: { duration: 0.01, delay: 0.2 } }}
        />
        {/* reveal edge that travels with the wipe */}
        {!reduce && (
          <motion.span
            aria-hidden="true"
            className="absolute top-0 h-full w-px bg-paper/80"
            initial={{ left: "0%", opacity: 1 }}
            animate={{ left: "100%", opacity: [1, 1, 0] }}
            transition={{ left: { duration: 1.25, ease: EASE, delay: 0.2 }, opacity: { duration: 1.25, delay: 0.2, times: [0, 0.9, 1] } }}
          />
        )}
        {/* accent baseline */}
        <motion.span
          aria-hidden="true"
          className="absolute -bottom-7 left-0 block h-px w-full origin-left bg-accent"
          initial={reduce ? false : { scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.35 }}
        />
      </motion.div>

      {/* bottom labels */}
      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.7 }}
        className="container-x absolute bottom-8 left-0 right-0 flex justify-between mono-label text-paper/45"
      >
        <span>L&A Projektbau GmbH</span>
        <span>47°31'N 8°40'E</span>
      </motion.div>
    </motion.div>
  );
};
